import { useState, useEffect, useCallback } from 'react'
import type {
  ArtefactTemplate,
  CollectorSettings,
  CreateArtefactTemplateInput,
  TestBuildInput,
  TestBuildResult,
  UpdateArtefactTemplateInput,
  UpsertCollectorSettingsInput,
} from '../types/artefact-templates'
import {
  listPlatformTemplates,
  getPlatformTemplate,
  createPlatformTemplate,
  updatePlatformTemplate,
  deletePlatformTemplate,
  upsertPlatformCollectorSettings,
  triggerPlatformTestBuild,
  downloadPlatformTestBuild,
} from '../services/admin-artefact-templates'

// =============================================================================
// usePlatformTemplates - List platform templates
// =============================================================================

interface UsePlatformTemplatesResult {
  templates: ArtefactTemplate[]
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

export function usePlatformTemplates(): UsePlatformTemplatesResult {
  const [templates, setTemplates] = useState<ArtefactTemplate[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetch = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await listPlatformTemplates()
      setTemplates(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch platform templates')
      setTemplates([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void fetch()
  }, [fetch])

  return { templates, loading, error, refetch: fetch }
}

// =============================================================================
// usePlatformTemplateMutations - CRUD mutations
// =============================================================================

export interface UsePlatformTemplateMutationsResult {
  creating: boolean
  updating: boolean
  deleting: boolean
  error: string | null
  create: (input: CreateArtefactTemplateInput) => Promise<ArtefactTemplate | null>
  update: (
    templateId: string,
    input: UpdateArtefactTemplateInput
  ) => Promise<ArtefactTemplate | null>
  remove: (templateId: string) => Promise<boolean>
  clearError: () => void
}

export function usePlatformTemplateMutations(): UsePlatformTemplateMutationsResult {
  const [creating, setCreating] = useState(false)
  const [updating, setUpdating] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const create = useCallback(
    async (input: CreateArtefactTemplateInput): Promise<ArtefactTemplate | null> => {
      setCreating(true)
      setError(null)
      try {
        return await createPlatformTemplate(input)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to create platform template')
        return null
      } finally {
        setCreating(false)
      }
    },
    []
  )

  const update = useCallback(
    async (
      templateId: string,
      input: UpdateArtefactTemplateInput
    ): Promise<ArtefactTemplate | null> => {
      setUpdating(true)
      setError(null)
      try {
        return await updatePlatformTemplate(templateId, input)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to update platform template')
        return null
      } finally {
        setUpdating(false)
      }
    },
    []
  )

  const remove = useCallback(async (templateId: string): Promise<boolean> => {
    setDeleting(true)
    setError(null)
    try {
      await deletePlatformTemplate(templateId)
      return true
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete platform template')
      return false
    } finally {
      setDeleting(false)
    }
  }, [])

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  return { creating, updating, deleting, error, create, update, remove, clearError }
}

// =============================================================================
// usePlatformCollectorSettings - Get/upsert settings for a platform template
// =============================================================================

interface UsePlatformCollectorSettingsResult {
  settings: CollectorSettings | null
  loading: boolean
  saving: boolean
  error: string | null
  refetch: () => Promise<void>
  save: (input: UpsertCollectorSettingsInput) => Promise<CollectorSettings | null>
}

export function usePlatformCollectorSettings(
  templateId: string | null
): UsePlatformCollectorSettingsResult {
  const [settings, setSettings] = useState<CollectorSettings | null>(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetch = useCallback(async () => {
    if (!templateId) {
      setSettings(null)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const template = await getPlatformTemplate(templateId)
      setSettings(template.collectorSettings)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch collector settings')
    } finally {
      setLoading(false)
    }
  }, [templateId])

  useEffect(() => {
    void fetch()
  }, [fetch])

  const save = useCallback(
    async (input: UpsertCollectorSettingsInput): Promise<CollectorSettings | null> => {
      if (!templateId) return null
      setSaving(true)
      setError(null)
      try {
        const data = await upsertPlatformCollectorSettings(templateId, input)
        setSettings(data)
        return data
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to save collector settings')
        return null
      } finally {
        setSaving(false)
      }
    },
    [templateId]
  )

  return { settings, loading, saving, error, refetch: fetch, save }
}

// =============================================================================
// usePlatformTestBuild - Build and download
// =============================================================================

interface UsePlatformTestBuildResult {
  building: boolean
  downloading: boolean
  error: string | null
  triggerBuild: (templateId: string, input: TestBuildInput) => Promise<TestBuildResult | null>
  download: (flowId: string) => Promise<boolean>
}

export function usePlatformTestBuild(): UsePlatformTestBuildResult {
  const [building, setBuilding] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const triggerBuild = useCallback(
    async (templateId: string, input: TestBuildInput): Promise<TestBuildResult | null> => {
      setBuilding(true)
      setError(null)
      try {
        return await triggerPlatformTestBuild(templateId, input)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to trigger test build')
        return null
      } finally {
        setBuilding(false)
      }
    },
    []
  )

  const download = useCallback(async (flowId: string): Promise<boolean> => {
    setDownloading(true)
    setError(null)
    try {
      await downloadPlatformTestBuild(flowId)
      return true
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to download collector')
      return false
    } finally {
      setDownloading(false)
    }
  }, [])

  return { building, downloading, error, triggerBuild, download }
}
